import { company } from '../data/company';
import { SITE_URL, absoluteUrl } from '../../lib/canonicalHost';
import type { CropSolution, Product, Category } from '../types';
import type { ProductFacts } from './productFacts';

/**
 * Structured-data builders for the public marketing site. Every JSON-LD block on the site is built
 * here — pages never hand-write a schema object — so the entity graph stays consistent and a fact
 * added to `data/company.ts` or the catalogue API reaches every page that states it.
 *
 * TWO RULES GOVERN THIS FILE.
 *
 * 1. ONE ENTITY PER THING, ADDRESSED BY @id. The company is `${SITE_URL}/#organization` and the site
 *    is `${SITE_URL}/#website`, everywhere. Repeating the organisation's name and address inline on
 *    ten pages tells a crawler it might be looking at ten companies; referencing one @id says these
 *    are all statements about one entity, which is what makes "what is GreenbayBio" answerable.
 *
 * 2. NOTHING THAT IS NOT VERIFIED. A schema field is a factual claim in machine-readable form, and an
 *    unverifiable one is worse than a missing one because it is stated with total confidence. So:
 *      - no `aggregateRating` / `review` — there are no reviews;
 *      - no `offers` / `price` / `availability` — the public site deliberately shows no price, and the
 *        public API does not even serve one;
 *      - no `manufacturer`. This used to claim Greenbay Biotech International manufactures every
 *        product. The technical documents do not support that for the whole range — Biovin is an
 *        EU-approved product, C4L Biofoliar was developed in South Africa, Hydretain is a USDA
 *        BioPreferred product — and only C4L's own datasheet says "manufactured and marketed by
 *        Greenbay". `brand` is the claim the evidence supports, so `brand` is what we publish;
 *      - no invented `sameAs` — `company.socials` is empty, so no social profiles are asserted;
 *      - no `priceRange` — the "$$" that used to sit in the LocalBusiness node was a guess.
 */

export { SITE_URL, absoluteUrl };

/** The 1200x630 social card that ships in public/og/ (the 512x304 logo is not a valid share image). */
export const DEFAULT_OG = '/og/default.webp';
export const DEFAULT_OG_WIDTH = 1200;
export const DEFAULT_OG_HEIGHT = 630;

/** Stable node ids. Everything that mentions the company or the site points at these. */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

const postalAddress = () => ({
  '@type': 'PostalAddress',
  streetAddress: `${company.address.line1}, ${company.address.line2}`,
  addressLocality: company.address.city,
  addressRegion: 'West Bengal',
  postalCode: company.address.postcode,
  addressCountry: 'IN',
});

export interface BreadcrumbItem {
  name: string;
  path: string;
}

/**
 * The company. `name` is the brand the site trades under (GreenbayBio) and `legalName` the registered
 * entity (Greenbay Biotech International Pvt Ltd.) — a crawler asked "what is Greenbay Biotech
 * International" and one asked "what is GreenbayBio" must reach the same answer, which is what
 * alternateName + legalName together do.
 */
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: company.name,
    legalName: company.legalName,
    alternateName: [company.legalName, company.shortName],
    url: `${SITE_URL}/`,
    logo: {
      '@type': 'ImageObject',
      url: absoluteUrl('/brand/greenbay-logo.png'),
      width: 1000,
      height: 594,
    },
    image: absoluteUrl(DEFAULT_OG),
    description: company.description,
    email: company.email,
    telephone: company.phoneDisplay,
    address: postalAddress(),
    areaServed: { '@type': 'Country', name: 'India' },
    knowsAbout: [
      'bio-fertilizers',
      'biostimulants',
      'soil biology',
      'phosphate solubilizing bacteria',
      'humic and fulvic substances',
      'foliar nutrition',
      'root-zone moisture management',
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        telephone: company.phoneDisplay,
        email: company.email,
        areaServed: 'IN',
        availableLanguage: ['en', 'hi'],
      },
    ],
  };
}

/** The website itself, published by the organisation. Drives the sitelinks search box. */
export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: company.name,
    alternateName: company.legalName,
    url: `${SITE_URL}/`,
    inLanguage: 'en-IN',
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/catalog?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };
}

/** The head office as a findable place. Same company — hence the parentOrganization reference. */
export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#headoffice`,
    parentOrganization: { '@id': ORG_ID },
    name: company.legalName,
    image: absoluteUrl(DEFAULT_OG),
    url: `${SITE_URL}/contact`,
    telephone: company.phoneDisplay,
    email: company.email,
    address: postalAddress(),
    areaServed: { '@type': 'Country', name: 'India' },
  };
}

export function breadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

/**
 * A product, as the catalogue actually records it.
 *
 * `facts` is the typed view of the admin-authored detail sections (see lib/productFacts) — the same
 * composition rows, specifications, dose summary, pack sizes and licence a visitor reads on the page.
 * Publishing them as `additionalProperty` is the difference between a crawler knowing a product named
 * AGN exists and being able to answer "what is AGN made from" or "how much AGN per acre for grapes".
 */
export function productSchema(product: Product, facts?: ProductFacts, category?: Category) {
  const url = absoluteUrl(`/catalog/${product.slug}`);
  const images = product.variants.map((v) => v.image).filter((s): s is string => !!s);
  const props: { '@type': 'PropertyValue'; name: string; value: string }[] = [];

  const takenProps = new Set<string>();
  const push = (name: string, value?: string | null) => {
    const v = (value ?? '').trim();
    // First statement wins. The composition row carries the fuller wording ("… (as per FCO)"), so it
    // is the one that survives when a specification row repeats the same parameter.
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (!v || !key || takenProps.has(key)) return;
    takenProps.add(key);
    props.push({ '@type': 'PropertyValue', name, value: v });
  };

  for (const row of facts?.compositionRows ?? []) push(row.parameter, row.value);
  for (const row of facts?.specifications ?? []) push(row.parameter, row.value);
  push('Application methods', facts?.applicationMethods?.join(', '));
  push('Dosage', facts?.dosageSummary);
  push('Recommended crops', product.recommendedCrops?.join(', '));
  push('Pack sizes', product.variants.map((v) => v.packSize).join(', '));
  push('Shelf life', facts?.shelfLife);
  push('Approvals and licence', facts?.licence);

  // The technical datasheets. `subjectOf` is the honest relation: the PDF is a document ABOUT the
  // product, not a property of it — and naming its language lets a crawler answer a Hindi query from
  // the Hindi sheet even though the page copy itself is English-only.
  const documents = (product.resources ?? []).map((r) => ({
    '@type': 'DigitalDocument',
    name: r.lang ? `${product.name} — ${r.label} (${r.lang})` : `${product.name} — ${r.label}`,
    url: absoluteUrl(r.url),
    encodingFormat: 'application/pdf',
    ...(r.lang ? { inLanguage: r.lang } : {}),
    about: { '@id': `${url}#product` },
    publisher: { '@id': ORG_ID },
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${url}#product`,
    name: product.name,
    url,
    description: product.shortDescription || product.purpose,
    ...(product.tagline ? { alternateName: `${product.name} — ${product.tagline}` } : {}),
    ...(images.length ? { image: images.map((i) => absoluteUrl(i)) } : {}),
    brand: { '@type': 'Brand', '@id': `${SITE_URL}/#brand`, name: company.name },
    ...(category ? { category: category.name } : {}),
    ...(props.length ? { additionalProperty: props } : {}),
    ...(documents.length ? { subjectOf: documents } : {}),
    ...(product.variants.length > 1
      ? {
          hasVariant: product.variants.map((v) => ({
            '@type': 'Product',
            name: `${product.name} ${v.packSize}`,
            ...(v.sku ? { sku: v.sku } : {}),
            size: v.packSize,
          })),
        }
      : product.variants[0]?.sku
        ? { sku: product.variants[0].sku }
        : {}),
    audience: {
      '@type': 'Audience',
      audienceType: 'Farmers, agricultural retailers and distributors in India',
      geographicArea: { '@type': 'Country', name: 'India' },
    },
  };
}

/** An educational article. The author is the ORGANISATION — we do not invent named agronomists. */
export function articleSchema(a: {
  slug: string;
  title: string;
  summary: string;
  updated?: string;
  section?: string;
}) {
  const url = absoluteUrl(`/knowledge/${a.slug}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: a.title,
    description: a.summary,
    url,
    mainEntityOfPage: url,
    inLanguage: 'en-IN',
    ...(a.section ? { articleSection: a.section } : {}),
    ...(a.updated ? { dateModified: a.updated } : {}),
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': WEBSITE_ID },
  };
}

export function faqSchema(items: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

/** A listing page (catalogue, a category, crop solutions) as a list of real urls. */
export function itemListSchema(name: string, path: string, items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${absoluteUrl(path)}#list`,
    name,
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      url: absoluteUrl(it.path),
    })),
  };
}

/** A collection page (category / crop programme) attached to the site and organisation. */
export function collectionPageSchema(opts: { name: string; description: string; path: string }) {
  const url = absoluteUrl(opts.path);
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${url}#page`,
    name: opts.name,
    description: opts.description,
    url,
    inLanguage: 'en-IN',
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORG_ID },
  };
}

/**
 * A crop programme. There is no Schema.org type for "the products documented for this crop", so this
 * is a CollectionPage whose `about` is the crop as a Thing and whose `mainEntity` is the product list
 * — factual, and it does not bend a type to mean something it does not.
 */
export function cropProgrammeSchema(crop: CropSolution, products: { name: string; slug: string }[]) {
  const url = absoluteUrl(`/crop-solutions/${crop.slug}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${url}#page`,
    name: `GreenbayBio programme for ${crop.name}`,
    ...(crop.intro ? { description: crop.intro } : {}),
    url,
    inLanguage: 'en-IN',
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORG_ID },
    about: { '@type': 'Thing', name: crop.name, description: `${crop.name} cultivation` },
    mainEntity: {
      '@type': 'ItemList',
      name: `Products documented for ${crop.name}`,
      numberOfItems: products.length,
      itemListElement: products.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: p.name,
        url: absoluteUrl(`/catalog/${p.slug}`),
      })),
    },
  };
}

/** The technical-document library as a list of real PDFs, each about a real product. */
export function documentListSchema(
  path: string,
  docs: { productName: string; productSlug: string; label: string; url: string; lang?: string }[],
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${absoluteUrl(path)}#documents`,
    name: 'GreenbayBio technical documents',
    numberOfItems: docs.length,
    itemListElement: docs.map((d, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'DigitalDocument',
        name: `${d.productName} — ${d.label}`,
        url: absoluteUrl(d.url),
        encodingFormat: 'application/pdf',
        ...(d.lang ? { inLanguage: d.lang } : {}),
        about: { '@id': `${absoluteUrl('/catalog/' + d.productSlug)}#product` },
        publisher: { '@id': ORG_ID },
      },
    })),
  };
}
